/**
 * Stats Grid Component
 *
 * Displays key metrics in a grid layout for the dashboard.
 */

import { cn } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { StatCard } from '@/components/ui/Card';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import type { DashboardStats } from '@/types';
import {
  Phone,
  CheckCircle2,
  AlertTriangle,
  Activity,
  Clock,
  Calendar,
  CreditCard,
  TrendingUp,
} from 'lucide-react';

// ============================================================================
// Component
// ============================================================================

export function StatsGrid() {
  const { stats, isLoading, error } = useDashboardStats();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-24 mb-4" />
              <div className="h-8 bg-slate-200 rounded w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) {
    Logger.warn(LogCategory.DATA, 'No stats available to display', 'StatsGrid');
    return null;
  }

  const statItems = [
    {
      title: 'Total Phone Numbers',
      value: stats.totalPhoneNumbers.toLocaleString(),
      icon: <Phone className="h-6 w-6" />,
      description: 'Numbers being monitored',
    },
    {
      title: 'Clean Numbers',
      value: stats.cleanNumbers.toLocaleString(),
      icon: <CheckCircle2 className="h-6 w-6" />,
    },
    {
      title: 'Flagged Numbers',
      value: stats.flaggedNumbers.toLocaleString(),
      icon: <AlertTriangle className="h-6 w-6" />,
    },
    {
      title: 'Flagged Rate',
      value: `${stats.flaggedPercentage.toFixed(1)}%`,
      icon: <TrendingUp className="h-6 w-6" />,
    },
    {
      title: 'Last 24h Activity',
      value: stats.last24hActivity.toLocaleString(),
      icon: <Clock className="h-6 w-6" />,
      description: 'Checks performed',
    },
    {
      title: 'Last 7 Days',
      value: stats.last7dActivity.toLocaleString(),
      icon: <Activity className="h-6 w-6" />,
      description: 'Checks performed',
    },
    {
      title: 'Credits Remaining',
      value: stats.creditsRemaining.toLocaleString(),
      icon: <CreditCard className="h-6 w-6" />,
      description: `${stats.creditsUsedThisMonth.toLocaleString()} used this month`,
    },
    {
      title: 'Last 30 Days',
      value: stats.last30dActivity.toLocaleString(),
      icon: <Calendar className="h-6 w-6" />,
      description: 'Checks performed',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {statItems.map((item, index) => (
        <StatCard
          key={index}
          title={item.title}
          value={item.value}
          icon={item.icon}
          description={item.description}
        />
      ))}
    </div>
  );
}
